import React, { useRef, useEffect, useCallback, useState, useContext, useMemo } from 'react';
import {
  RecordingTimelineContext,
  STATUS_COLORS,
  hhmmssToSeconds,
  minuteStatuses,
} from '../recording-timeline/TimelineContext';
import { colors } from '../../constants/colors';
import { setGlobalZoom } from "../../redux/reducers/service-reducer";
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
type VideoSegment = { start: number; end: number };
interface TimelineBarProps {
  value: number;
  onChange: (timeInSeconds: number) => void;
  segments: VideoSegment[][];
  bufferedUntil?: number;
  // Extra strips (Image mode, board offline) keep their own zoom: the zoom in
  // redux is shared with the video strip and the chart, and a second strip
  // resetting or changing it moved the hidden video strip off its pan range.
  independentZoom?: boolean;
}
const TOTAL = 86399;
// Canvas geometry. The header row above the canvas overlaps its top 10px
// (marginBottom -10), so everything clickable starts below that.
const TRACK_Y = 16;
const TRACK_H = 14;
const CANVAS_H = 45;
const LABEL_Y = TRACK_Y + TRACK_H + 2;
// Snapshot pins: a click on a pin head (just above the track) opens that
// photo; a click on the track itself always seeks.
const PIN_HEAD_PX = 6;
const PIN_HIT_PX = 5;
const MIN_ZOOM = 1;
const MAX_ZOOM = 20;

const ProgressScale: React.FC<TimelineBarProps> = ({ value, onChange, segments, bufferedUntil, independentZoom = false }) => {
  const dispatch = useDispatch();
  const { globalZoom } = useSelector((state: RootState) => state.service);
  const { activeCarDetails, selectedDate } = useSelector(
    (state: RootState) => state.service
  );
  const [selectZoom, setSelectZoom] = useState(independentZoom ? 1 : globalZoom);
  const zoomRef = useRef(independentZoom ? 1 : globalZoom);
  const prevValueRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panRef = useRef(0);
  const dragging = useRef(false);
  const dragX0 = useRef(0);
  const dragPan0 = useRef(0);
  const dragMoved = useRef(false);
  const pinchD0 = useRef(0);
  const pinchZ0 = useRef(1);
  const touchX0 = useRef(0);
  const touchPan0 = useRef(0);
  const [displayTime, setDisplayTime] = useState('');
  // When the server timeline is available the strip is coloured from it (what
  // the board actually recorded, minute by minute) and carries the snapshot
  // markers; without it the strip falls back to the playable segments.
  const timeline = useContext(RecordingTimelineContext);
  const serverMinutes = useMemo(
    () => (timeline && timeline.slots.length ? minuteStatuses(timeline.slots, timeline.date) : null),
    [timeline?.slots, timeline?.date]
  );
  const snapshots = timeline?.snapshots;
  const selectedSnapshot = timeline?.selectedSnapshot;
  useEffect(() => {
    zoomRef.current = 1;
    setSelectZoom(1);
    panRef.current = 0;
    if (!independentZoom) dispatch(setGlobalZoom(1));
    requestAnimationFrame(() => {
      draw();
    });
  }, [activeCarDetails, selectedDate, dispatch]);

  const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
  const snapPan = () => { panRef.current = clamp(panRef.current, 0, TOTAL - TOTAL / zoomRef.current); };
  const s2x = (t: number, W: number) => ((t - panRef.current) / (TOTAL / zoomRef.current)) * W;
  const x2s = (x: number, W: number) => panRef.current + (x / W) * (TOTAL / zoomRef.current);

  const fmtTime = (s: number) =>
    new Date(s * 1000).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true, timeZone: 'UTC',
    });

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const W = wrap.clientWidth || 680;
    const H = CANVAS_H;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);
    const trackY = TRACK_Y, trackH = TRACK_H;
    const z = zoomRef.current;
    const span = TOTAL / z;
    const startHr = Math.floor(panRef.current / 3600);
    const endHr = Math.ceil((panRef.current + span) / 3600);
    ctx.fillStyle = '#e0e0e0';
    ctx.beginPath();
    (ctx as any).roundRect(0, trackY, W, trackH, 3);
    ctx.fill();
    const segColors = ['#f59200', '#f5b800'];
    if (serverMinutes) {
      // runs of equal status, one fillRect each
      let m = 0;
      while (m < 1440) {
        const st = serverMinutes[m];
        let e = m + 1;
        while (e < 1440 && serverMinutes[e] === st) e++;
        if (st) {
          const sx = s2x(m * 60, W), ex = s2x(Math.min(e * 60, TOTAL), W);
          if (ex >= 0 && sx <= W) {
            ctx.fillStyle = STATUS_COLORS[st] || '#e0e0e0';
            ctx.fillRect(Math.max(0, sx), trackY, Math.min(W, ex) - Math.max(0, sx), trackH);
          }
        }
        m = e;
      }
      if (bufferedUntil && bufferedUntil > value) {
        const startX = s2x(value, W);
        ctx.fillStyle = "rgba(0,122,255,0.55)";
        ctx.fillRect(startX, trackY, s2x(bufferedUntil, W) - startX, trackH);
      }
    }
    (serverMinutes ? [] : segments).forEach((row, ri) => {
      ctx.fillStyle = segColors[ri % 2];
      row.forEach((sg) => {
        const s = sg.start, e = sg.end < sg.start ? 86399 : sg.end;
        const sx = s2x(s, W), ex = s2x(e, W);
        if (ex < 0 || sx > W) return;
        ctx.fillRect(Math.max(0, sx), trackY, Math.min(W, ex) - Math.max(0, sx), trackH);
      });
      if (
        bufferedUntil &&
        bufferedUntil > value
      ) {
        const startX = s2x(value, W);
        const endX = s2x(
          bufferedUntil,
          W
        );
        ctx.fillStyle =
          "rgba(0,122,255,0.55)";
        ctx.fillRect(
          startX,
          trackY,
          endX - startX,
          trackH
        );
      }
    });
    ctx.save();
    ctx.beginPath();
    (ctx as any).roundRect(0, trackY, W, trackH, 3);
    ctx.clip();
    if (z >= 3) {
      let ms = 30;
      if (z >= 8) ms = 10;
      if (z >= 12) ms = 5;
      const t0 = Math.floor(panRef.current / 60) * 60;
      for (let t = t0; t <= panRef.current + span; t += 60) {
        if (t % 3600 === 0) continue;
        const m = Math.round((t % 3600) / 60);
        if (m % ms !== 0) continue;
        const x = s2x(t, W);
        if (x < 0 || x > W) continue;
        ctx.fillStyle = t % 1800 === 0 ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.10)';
        ctx.fillRect(x - 0.5, trackY, 1, trackH);
      }
    }
    for (let hr = startHr; hr <= endHr; hr++) {
      const t = hr * 3600;
      if (t > TOTAL) continue;
      const x = s2x(t, W);
      if (x < -1 || x > W + 1) continue;
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.fillRect(x - 0.5, trackY, 1, trackH);
    }
    ctx.restore();
    ctx.textBaseline = 'top';
    if (z >= 3) {
      let ls = 30;
      if (z >= 8) ls = 10;
      if (z >= 12) ls = 5;
      ctx.font = '9px -apple-system,sans-serif';
      const t0 = Math.floor(panRef.current / 60) * 60;
      for (let t = t0; t <= panRef.current + span; t += 60) {
        if (t % 3600 === 0) continue;
        const m = Math.round((t % 3600) / 60);
        if (m % ls !== 0) continue;
        const x = s2x(t, W);
        if (x < 12 || x > W - 12) continue;
        ctx.textAlign = 'center';
        ctx.fillStyle = '#888';
        ctx.fillText(':' + String(m).padStart(2, '0'), x, LABEL_Y);
      }
    }

    // --- Hour labels: thin them out when there isn't enough room per label ---
    // (fixes overlapping "12am1am2am3am..." on narrow / mobile widths)
    ctx.font = '500 10px -apple-system,sans-serif';
    const hourStepPx = (3600 / span) * W; // pixel width of a single hour at current zoom/width
    const MIN_LABEL_PX = 34; // approx min horizontal space a label like "12am" needs to not collide
    const hourSkip = Math.max(1, Math.ceil(MIN_LABEL_PX / hourStepPx));
    for (let hr = startHr; hr <= endHr; hr++) {
      const t = hr * 3600;
      if (t > TOTAL) continue;
      if (hr % hourSkip !== 0) continue;
      const x = s2x(t, W);
      if (x < 0 || x > W) continue;
      const h24 = hr % 24;
      const lbl = h24 === 0 ? '12am' : h24 === 12 ? '12pm' : h24 < 12 ? `${h24}am` : `${h24 - 12}pm`;
      let align: CanvasTextAlign = 'center';
      if (x < 18) align = 'left';
      else if (x > W - 18) align = 'right';
      ctx.textAlign = align;
      ctx.fillStyle = '#555';
      ctx.fillText(lbl, align === 'left' ? x + 2 : align === 'right' ? x - 2 : x, LABEL_Y);
    }
    // Snapshot markers (one per half hour the board sent a photo). Click one to
    // open that photo; the selected one is drawn thicker.
    if (snapshots && snapshots.length) {
      snapshots.forEach((sn) => {
        const x = s2x(hhmmssToSeconds(sn.time), W);
        if (x < -2 || x > W + 2) return;
        const on = sn.time === selectedSnapshot;
        ctx.fillStyle = on ? '#7f1d1d' : '#dc2626';
        ctx.fillRect(x - (on ? 3 : 2.5), trackY - PIN_HEAD_PX, on ? 6 : 5, PIN_HEAD_PX); // pin head (click target)
        ctx.fillRect(x - (on ? 1 : 0.5), trackY, on ? 2 : 1, trackH);  // pin line
      });
    }
    const cx = s2x(value, W);
    if (cx >= -6 && cx <= W + 6) {
      ctx.strokeStyle = "#e84040";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(cx, trackY - 4);
      ctx.lineTo(cx, trackY + trackH + 3);
      ctx.stroke();
      ctx.fillStyle = "#e84040";
      ctx.beginPath();
      ctx.moveTo(cx - 5, trackY - 4);
      ctx.lineTo(cx + 5, trackY - 4);
      ctx.lineTo(cx, trackY + 2);
      ctx.closePath();
      ctx.fill();
    }
    if (z > 1) {
      ctx.fillStyle = '#e8e8e8';
      ctx.beginPath();
      (ctx as any).roundRect(0, H - 3, W, 3, 0);
      ctx.fill();
      ctx.fillStyle = 'rgba(245,146,0,0.6)';
      ctx.beginPath();
      (ctx as any).roundRect((panRef.current / TOTAL) * W, H - 3, Math.max(6, (span / TOTAL) * W), 3, 0);
      ctx.fill();
    }
    setDisplayTime(fmtTime(value));
  }, [value, segments, serverMinutes, snapshots, selectedSnapshot, bufferedUntil]);

  const applyZoom = useCallback(
    (nz: number, focal?: number) => {
      const prev = TOTAL / zoomRef.current;
      const updatedZoom = clamp(
        nz,
        MIN_ZOOM,
        MAX_ZOOM
      );
      zoomRef.current = updatedZoom;
      setSelectZoom(updatedZoom);
      if (!independentZoom) dispatch(setGlobalZoom(updatedZoom));
      const ns = TOTAL / zoomRef.current;
      if (focal != null) {
        panRef.current =
          focal -
          ((focal - panRef.current) / prev) * ns;
      }
      snapPan();
      draw();
    },
    [draw, dispatch, independentZoom]
  );

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onD = (e: MouseEvent) => { dragging.current = true; dragX0.current = e.clientX; dragPan0.current = panRef.current; dragMoved.current = false; canvasRef.current!.style.cursor = 'grabbing'; };
    const onM = (e: MouseEvent) => {
      if (!dragging.current) return;
      const d = e.clientX - dragX0.current;
      if (Math.abs(d) > 3) dragMoved.current = true;
      panRef.current = dragPan0.current - (d / el.clientWidth) * (TOTAL / zoomRef.current);
      snapPan(); draw();
    };
    const onU = (e: MouseEvent) => {
      if (!dragging.current) return;
      dragging.current = false; canvasRef.current!.style.cursor = 'pointer';
      if (!dragMoved.current) {
        const r = el.getBoundingClientRect();
        const px = e.clientX - r.left;
        // Only a click on a pin head (the band above the track) opens a
        // photo; anywhere on the track still seeks, however dense the pins.
        const py = e.clientY - r.top;
        if (timeline && timeline.snapshots.length && py >= TRACK_Y - PIN_HEAD_PX - 2 && py <= TRACK_Y + 1) {
          let hit: string | null = null;
          let best = PIN_HIT_PX + 1;
          timeline.snapshots.forEach((sn) => {
            const d = Math.abs(s2x(hhmmssToSeconds(sn.time), r.width) - px);
            if (d < best) { best = d; hit = sn.time; }
          });
          if (hit) { timeline.openSnapshot(hit); return; }
        }
        onChange(
          clamp(
            Number(
              x2s(
                px,
                r.width
              ).toFixed(3)
            ),
            0,
            TOTAL
          )
        );
      }
    };
    el.addEventListener('mousedown', onD);
    window.addEventListener('mousemove', onM);
    window.addEventListener('mouseup', onU);
    return () => { el.removeEventListener('mousedown', onD); window.removeEventListener('mousemove', onM); window.removeEventListener('mouseup', onU); };
  }, [draw, onChange, timeline]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onS = (e: TouchEvent) => {
      if (e.touches.length === 1) { touchX0.current = e.touches[0].clientX; touchPan0.current = panRef.current; }
      else if (e.touches.length === 2) { pinchD0.current = Math.abs(e.touches[0].clientX - e.touches[1].clientX); pinchZ0.current = zoomRef.current; }
    };
    const onM = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1) { panRef.current = touchPan0.current - ((e.touches[0].clientX - touchX0.current) / el.clientWidth) * (TOTAL / zoomRef.current); snapPan(); draw(); }
      else if (e.touches.length === 2) {
        const d = Math.abs(e.touches[0].clientX - e.touches[1].clientX);
        const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - el.getBoundingClientRect().left;
        applyZoom(pinchZ0.current * (d / pinchD0.current), x2s(mx, el.clientWidth));
      }
    };
    el.addEventListener('touchstart', onS, { passive: true });
    el.addEventListener('touchmove', onM, { passive: false });
    return () => { el.removeEventListener('touchstart', onS); el.removeEventListener('touchmove', onM); };
  }, [draw, applyZoom]);

  useEffect(() => {
    const ro = new ResizeObserver(() => draw());
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [draw]);

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    if (independentZoom) return;
    zoomRef.current = globalZoom;
    setSelectZoom(globalZoom);
    // the zoom may have changed elsewhere; keep the pan inside the new range
    snapPan();
    requestAnimationFrame(() => {
      draw();
    });
  }, [globalZoom, draw, independentZoom]);

  useEffect(() => {
    zoomRef.current = 1;
    setSelectZoom(1);
    panRef.current = 0;
    if (!independentZoom) {
      dispatch(setGlobalZoom(1));
      onChange(0);
    }
    setDisplayTime("12:00:00 AM");
    requestAnimationFrame(() => {
      draw();
    });
  }, [activeCarDetails, selectedDate]);

  useEffect(() => {
    // Follow the playhead only when it moves: draw also changes on every
    // timeline poll and buffer update, and must not yank a user's pan back.
    if (prevValueRef.current === value) return;
    prevValueRef.current = value;
    const visibleSpan = TOTAL / zoomRef.current;
    if (
      value < panRef.current ||
      value > panRef.current + visibleSpan
    ) {
      panRef.current = Math.max(
        0,
        Math.min(
          value - visibleSpan / 2,
          TOTAL - visibleSpan
        )
      );
      draw();
    }
  }, [value, draw]);

  return (
    <div style={{
      border: `1.5px solid ${colors.orange}`,
      borderRadius: 8,
      overflow: 'hidden',
      background: '#fff',
      width: '100%',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0px 10px',
        background: '#fff',
        position: 'relative',
        marginBottom: "-10px",
      }}>
        <div style={{ width: 60, visibility: 'hidden' }} />
        <div style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e84040', flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.2px', whiteSpace: 'nowrap' }}>
            {displayTime}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "2px 8px",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 20,
            background: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            minWidth: 140,
            height: 28,
          }}
        >
          <ZoomOutRoundedIcon
            onClick={() => {
              if (zoomRef.current > MIN_ZOOM) {
                applyZoom(
                  zoomRef.current - 1,
                  panRef.current + (TOTAL / zoomRef.current) / 2
                );
              }
            }}
            style={{
              fontSize: 16,
              color: zoomRef.current <= MIN_ZOOM ? "#bbb" : "#666",
              cursor: zoomRef.current <= MIN_ZOOM ? "not-allowed" : "pointer",
              transition: "0.2s",
              flexShrink: 0,
            }}
          />
          <input
            type="range"
            min={1}
            max={20}
            step={1}
            value={selectZoom}
            onChange={(e) => {
              applyZoom(
                Number(e.target.value),
                panRef.current + (TOTAL / zoomRef.current) / 2
              );
            }}
            style={{
              flex: 1,
              cursor: "pointer",
              accentColor: "#f59200",
              height: 3,
              width: 50
            }}
          />
          <ZoomInRoundedIcon
            onClick={() => {
              if (zoomRef.current < MAX_ZOOM) {
                applyZoom(
                  zoomRef.current + 1,
                  panRef.current + (TOTAL / zoomRef.current) / 2
                );
              }
            }}
            style={{
              fontSize: 16,
              color: zoomRef.current >= MAX_ZOOM ? "#bbb" : "#666",
              cursor: zoomRef.current >= MAX_ZOOM ? "not-allowed" : "pointer",
              transition: "0.2s",
              flexShrink: 0,
            }}
          />
          <div
            style={{
              minWidth: 30,
              height: 20,
              borderRadius: 10,
              background: "#f59200",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 700,
              padding: "0 6px",
              userSelect: "none",
              flexShrink: 0,
            }}
          >
            {selectZoom}x
          </div>
        </div>
      </div>
      <div ref={wrapRef} style={{ width: '100%', cursor: 'pointer' }}>
        <canvas ref={canvasRef} style={{ display: 'block' }} />
      </div>
    </div>
  );
};

export default React.memo(ProgressScale);

// import React, { useRef, useEffect, useCallback, useState } from 'react';
// import { colors } from '../../constants/colors';
// import { setGlobalZoom } from "../../redux/reducers/service-reducer";
// import { useDispatch, useSelector } from 'react-redux';
// import { RootState } from '../../redux/store';
// import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
// import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
// type VideoSegment = { start: number; end: number };
// interface TimelineBarProps {
//   value: number;
//   onChange: (timeInSeconds: number) => void;
//   segments: VideoSegment[][];
//   bufferedUntil?: number;
// }
// const TOTAL = 86399;
// const MIN_ZOOM = 1;
// const MAX_ZOOM = 20;

// const ProgressScale: React.FC<TimelineBarProps> = ({ value, onChange, segments, bufferedUntil }) => {
//   const dispatch = useDispatch();
//   const { globalZoom } = useSelector((state: RootState) => state.service);
//   const { activeCarDetails, selectedDate } = useSelector(
//     (state: RootState) => state.service
//   );
//   const [selectZoom, setSelectZoom] = useState(globalZoom);
//   const zoomRef = useRef(globalZoom);
//   const canvasRef = useRef<HTMLCanvasElement>(null);
//   const wrapRef = useRef<HTMLDivElement>(null);
//   const panRef = useRef(0);
//   const dragging = useRef(false);
//   const dragX0 = useRef(0);
//   const dragPan0 = useRef(0);
//   const dragMoved = useRef(false);
//   const pinchD0 = useRef(0);
//   const pinchZ0 = useRef(1);
//   const touchX0 = useRef(0);
//   const touchPan0 = useRef(0);
//   const [displayTime, setDisplayTime] = useState('');
//   useEffect(() => {
//     zoomRef.current = 1;
//     setSelectZoom(1);
//     panRef.current = 0;
//     dispatch(setGlobalZoom(1));
//     requestAnimationFrame(() => {
//       draw();
//     });
//   }, [activeCarDetails, selectedDate, dispatch]);

//   const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
//   const snapPan = () => { panRef.current = clamp(panRef.current, 0, TOTAL - TOTAL / zoomRef.current); };
//   const s2x = (t: number, W: number) => ((t - panRef.current) / (TOTAL / zoomRef.current)) * W;
//   const x2s = (x: number, W: number) => panRef.current + (x / W) * (TOTAL / zoomRef.current);

//   const fmtTime = (s: number) =>
//     new Date(s * 1000).toLocaleTimeString('en-US', {
//       hour: '2-digit', minute: '2-digit', second: '2-digit',
//       hour12: true, timeZone: 'UTC',
//     });

//   const draw = useCallback(() => {
//     const canvas = canvasRef.current;
//     const wrap = wrapRef.current;
//     if (!canvas || !wrap) return;
//     const dpr = window.devicePixelRatio || 1;
//     const W = wrap.clientWidth || 680;
//     const H = 35;
//     canvas.width = W * dpr;
//     canvas.height = H * dpr;
//     canvas.style.width = `${W}px`;
//     canvas.style.height = `${H}px`;
//     const ctx = canvas.getContext('2d')!;
//     ctx.scale(dpr, dpr);
//     ctx.clearRect(0, 0, W, H);
//     const trackY = 6, trackH = 14;
//     const z = zoomRef.current;
//     const span = TOTAL / z;
//     const startHr = Math.floor(panRef.current / 3600);
//     const endHr = Math.ceil((panRef.current + span) / 3600);
//     ctx.fillStyle = '#e0e0e0';
//     ctx.beginPath();
//     (ctx as any).roundRect(0, trackY, W, trackH, 3);
//     ctx.fill();
//     const segColors = ['#f59200', '#f5b800'];
//     segments.forEach((row, ri) => {
//       ctx.fillStyle = segColors[ri % 2];
//       row.forEach((sg) => {
//         const s = sg.start, e = sg.end < sg.start ? 86399 : sg.end;
//         const sx = s2x(s, W), ex = s2x(e, W);
//         if (ex < 0 || sx > W) return;
//         ctx.fillRect(Math.max(0, sx), trackY, Math.min(W, ex) - Math.max(0, sx), trackH);
//       });
//       if (
//         bufferedUntil &&
//         bufferedUntil > value
//       ) {
//         const startX = s2x(value, W);
//         const endX = s2x(
//           bufferedUntil,
//           W
//         );
//         ctx.fillStyle =
//           "rgba(0,122,255,0.55)";
//         ctx.fillRect(
//           startX,
//           trackY,
//           endX - startX,
//           trackH
//         );
//       }
//     });
//     ctx.save();
//     ctx.beginPath();
//     (ctx as any).roundRect(0, trackY, W, trackH, 3);
//     ctx.clip();
//     if (z >= 3) {
//       let ms = 30;
//       if (z >= 8) ms = 10;
//       if (z >= 12) ms = 5;
//       const t0 = Math.floor(panRef.current / 60) * 60;
//       for (let t = t0; t <= panRef.current + span; t += 60) {
//         if (t % 3600 === 0) continue;
//         const m = Math.round((t % 3600) / 60);
//         if (m % ms !== 0) continue;
//         const x = s2x(t, W);
//         if (x < 0 || x > W) continue;
//         ctx.fillStyle = t % 1800 === 0 ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.10)';
//         ctx.fillRect(x - 0.5, trackY, 1, trackH);
//       }
//     }
//     for (let hr = startHr; hr <= endHr; hr++) {
//       const t = hr * 3600;
//       if (t > TOTAL) continue;
//       const x = s2x(t, W);
//       if (x < -1 || x > W + 1) continue;
//       ctx.fillStyle = 'rgba(0,0,0,0.25)';
//       ctx.fillRect(x - 0.5, trackY, 1, trackH);
//     }
//     ctx.restore();
//     ctx.textBaseline = 'top';
//     if (z >= 3) {
//       let ls = 30;
//       if (z >= 8) ls = 10;
//       if (z >= 12) ls = 5;
//       ctx.font = '9px -apple-system,sans-serif';
//       const t0 = Math.floor(panRef.current / 60) * 60;
//       for (let t = t0; t <= panRef.current + span; t += 60) {
//         if (t % 3600 === 0) continue;
//         const m = Math.round((t % 3600) / 60);
//         if (m % ls !== 0) continue;
//         const x = s2x(t, W);
//         if (x < 12 || x > W - 12) continue;
//         ctx.textAlign = 'center';
//         ctx.fillStyle = '#888';
//         ctx.fillText(':' + String(m).padStart(2, '0'), x, 22);
//       }
//     }

//     ctx.font = '500 10px -apple-system,sans-serif';
//     for (let hr = startHr; hr <= endHr; hr++) {
//       const t = hr * 3600;
//       if (t > TOTAL) continue;
//       const x = s2x(t, W);
//       if (x < 0 || x > W) continue;
//       const h24 = hr % 24;
//       const lbl = h24 === 0 ? '12am' : h24 === 12 ? '12pm' : h24 < 12 ? `${h24}am` : `${h24 - 12}pm`;
//       let align: CanvasTextAlign = 'center';
//       if (x < 18) align = 'left';
//       else if (x > W - 18) align = 'right';
//       ctx.textAlign = align;
//       ctx.fillStyle = '#555';
//       ctx.fillText(lbl, align === 'left' ? x + 2 : align === 'right' ? x - 2 : x, 22);
//     }
//     const cx = s2x(value, W);
//     // if (cx >= -6 && cx <= W + 6) {
//     //   ctx.strokeStyle = '#e84040';
//     //   ctx.lineWidth = 2;
//     //   ctx.lineCap = 'round';
//     //   ctx.beginPath();
//     //   ctx.moveTo(cx, trackY - 4);
//     //   ctx.lineTo(cx, trackY + trackH + 3);
//     //   ctx.stroke();
//     //   ctx.fillStyle = '#e84040';
//     //   ctx.beginPath();
//     //   ctx.moveTo(cx - 5, trackY - 4);
//     //   ctx.lineTo(cx + 5, trackY - 4);
//     //   ctx.lineTo(cx, trackY + 2);
//     //   ctx.closePath();
//     //   ctx.fill();
//     // }
//     if (cx >= -6 && cx <= W + 6) {
//       ctx.strokeStyle = "#e84040";
//       ctx.lineWidth = 2;
//       ctx.lineCap = "round";
//       ctx.beginPath();
//       ctx.moveTo(cx, trackY - 4);
//       ctx.lineTo(cx, trackY + trackH + 3);
//       ctx.stroke();
//       ctx.fillStyle = "#e84040";
//       ctx.beginPath();
//       ctx.moveTo(cx - 5, trackY - 4);
//       ctx.lineTo(cx + 5, trackY - 4);
//       ctx.lineTo(cx, trackY + 2);
//       ctx.closePath();
//       ctx.fill();
//     }
//     if (z > 1) {
//       ctx.fillStyle = '#e8e8e8';
//       ctx.beginPath();
//       (ctx as any).roundRect(0, H - 3, W, 3, 0);
//       ctx.fill();
//       ctx.fillStyle = 'rgba(245,146,0,0.6)';
//       ctx.beginPath();
//       (ctx as any).roundRect((panRef.current / TOTAL) * W, H - 3, Math.max(6, (span / TOTAL) * W), 3, 0);
//       ctx.fill();
//     }
//     setDisplayTime(fmtTime(value));
//   }, [value, segments]);

//   const applyZoom = useCallback(
//     (nz: number, focal?: number) => {
//       const prev = TOTAL / zoomRef.current;
//       const updatedZoom = clamp(
//         nz,
//         MIN_ZOOM,
//         MAX_ZOOM
//       );
//       zoomRef.current = updatedZoom;
//       setSelectZoom(updatedZoom);
//       dispatch(setGlobalZoom(updatedZoom));
//       const ns = TOTAL / zoomRef.current;
//       if (focal != null) {
//         panRef.current =
//           focal -
//           ((focal - panRef.current) / prev) * ns;
//       }
//       snapPan();
//       draw();
//     },
//     [draw, dispatch]
//   );

//   useEffect(() => {
//     const el = wrapRef.current;
//     if (!el) return;
//     const onD = (e: MouseEvent) => { dragging.current = true; dragX0.current = e.clientX; dragPan0.current = panRef.current; dragMoved.current = false; canvasRef.current!.style.cursor = 'grabbing'; };
//     const onM = (e: MouseEvent) => {
//       if (!dragging.current) return;
//       const d = e.clientX - dragX0.current;
//       if (Math.abs(d) > 3) dragMoved.current = true;
//       panRef.current = dragPan0.current - (d / el.clientWidth) * (TOTAL / zoomRef.current);
//       snapPan(); draw();
//     };
//     const onU = (e: MouseEvent) => {
//       if (!dragging.current) return;
//       dragging.current = false; canvasRef.current!.style.cursor = 'pointer';
//       if (!dragMoved.current) {
//         const r = el.getBoundingClientRect();
//         onChange(
//           clamp(
//             Number(
//               x2s(
//                 e.clientX - r.left,
//                 r.width
//               ).toFixed(3)
//             ),
//             0,
//             TOTAL
//           )
//         );
//       }
//     };
//     el.addEventListener('mousedown', onD);
//     window.addEventListener('mousemove', onM);
//     window.addEventListener('mouseup', onU);
//     return () => { el.removeEventListener('mousedown', onD); window.removeEventListener('mousemove', onM); window.removeEventListener('mouseup', onU); };
//   }, [draw, onChange]);

//   useEffect(() => {
//     const el = wrapRef.current;
//     if (!el) return;
//     const onS = (e: TouchEvent) => {
//       if (e.touches.length === 1) { touchX0.current = e.touches[0].clientX; touchPan0.current = panRef.current; }
//       else if (e.touches.length === 2) { pinchD0.current = Math.abs(e.touches[0].clientX - e.touches[1].clientX); pinchZ0.current = zoomRef.current; }
//     };
//     const onM = (e: TouchEvent) => {
//       e.preventDefault();
//       if (e.touches.length === 1) { panRef.current = touchPan0.current - ((e.touches[0].clientX - touchX0.current) / el.clientWidth) * (TOTAL / zoomRef.current); snapPan(); draw(); }
//       else if (e.touches.length === 2) {
//         const d = Math.abs(e.touches[0].clientX - e.touches[1].clientX);
//         const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - el.getBoundingClientRect().left;
//         applyZoom(pinchZ0.current * (d / pinchD0.current), x2s(mx, el.clientWidth));
//       }
//     };
//     el.addEventListener('touchstart', onS, { passive: true });
//     el.addEventListener('touchmove', onM, { passive: false });
//     return () => { el.removeEventListener('touchstart', onS); el.removeEventListener('touchmove', onM); };
//   }, [draw, applyZoom]);

//   useEffect(() => {
//     const ro = new ResizeObserver(() => draw());
//     if (wrapRef.current) ro.observe(wrapRef.current);
//     return () => ro.disconnect();
//   }, [draw]);

//   useEffect(() => { draw(); }, [draw]);

//   useEffect(() => {
//     zoomRef.current = globalZoom;
//     setSelectZoom(globalZoom);
//     requestAnimationFrame(() => {
//       draw();
//     });
//   }, [globalZoom, draw]);

//   useEffect(() => {
//     zoomRef.current = 1;
//     setSelectZoom(1);
//     dispatch(setGlobalZoom(1));
//     panRef.current = 0;
//     onChange(0);
//     setDisplayTime("12:00:00 AM");
//     requestAnimationFrame(() => {
//       draw();
//     });
//   }, [activeCarDetails, selectedDate]);

//   useEffect(() => {
//     const visibleSpan = TOTAL / zoomRef.current;
//     if (
//       value < panRef.current ||
//       value > panRef.current + visibleSpan
//     ) {
//       panRef.current = Math.max(
//         0,
//         Math.min(
//           value - visibleSpan / 2,
//           TOTAL - visibleSpan
//         )
//       );
//       draw();
//     }
//   }, [value, draw]);

//   return (
//     <div style={{
//       border: `1.5px solid ${colors.orange}`,
//       borderRadius: 8,
//       overflow: 'hidden',
//       background: '#fff',
//       width: '100%',
//     }}>
//       <div style={{
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'space-between',
//         padding: '0px 10px',
//         background: '#fff',
//         position: 'relative',
//         marginBottom: "-10px",
//       }}>
//         <div style={{ width: 60, visibility: 'hidden' }} />
//         <div style={{
//           position: 'absolute', left: '50%', transform: 'translateX(-50%)',
//           display: 'flex', alignItems: 'center', gap: 6,
//         }}>
//           <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e84040', flexShrink: 0 }} />
//           <span style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.2px', whiteSpace: 'nowrap' }}>
//             {displayTime}
//           </span>
//         </div>
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             gap: 6,
//             padding: "2px 8px",
//             border: "1px solid rgba(0,0,0,0.08)",
//             borderRadius: 20,
//             background: "#fff",
//             boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
//             minWidth: 140,
//             height: 28,
//           }}
//         >
//           <ZoomOutRoundedIcon
//             onClick={() => {
//               if (zoomRef.current > MIN_ZOOM) {
//                 applyZoom(
//                   zoomRef.current - 1,
//                   panRef.current + (TOTAL / zoomRef.current) / 2
//                 );
//               }
//             }}
//             style={{
//               fontSize: 16,
//               color: zoomRef.current <= MIN_ZOOM ? "#bbb" : "#666",
//               cursor: zoomRef.current <= MIN_ZOOM ? "not-allowed" : "pointer",
//               transition: "0.2s",
//               flexShrink: 0,
//             }}
//           />
//           <input
//             type="range"
//             min={1}
//             max={20}
//             step={1}
//             value={selectZoom}
//             onChange={(e) => {
//               applyZoom(
//                 Number(e.target.value),
//                 panRef.current + (TOTAL / zoomRef.current) / 2
//               );
//             }}
//             style={{
//               flex: 1,
//               cursor: "pointer",
//               accentColor: "#f59200",
//               height: 3,
//               width: 50
//             }}
//           />
//           <ZoomInRoundedIcon
//             onClick={() => {
//               if (zoomRef.current < MAX_ZOOM) {
//                 applyZoom(
//                   zoomRef.current + 1,
//                   panRef.current + (TOTAL / zoomRef.current) / 2
//                 );
//               }
//             }}
//             style={{
//               fontSize: 16,
//               color: zoomRef.current >= MAX_ZOOM ? "#bbb" : "#666",
//               cursor: zoomRef.current >= MAX_ZOOM ? "not-allowed" : "pointer",
//               transition: "0.2s",
//               flexShrink: 0,
//             }}
//           />
//           <div
//             style={{
//               minWidth: 30,
//               height: 20,
//               borderRadius: 10,
//               background: "#f59200",
//               color: "#fff",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               fontSize: 10,
//               fontWeight: 700,
//               padding: "0 6px",
//               userSelect: "none",
//               flexShrink: 0,
//             }}
//           >
//             {selectZoom}x
//           </div>
//         </div>
//       </div>
//       <div ref={wrapRef} style={{ width: '100%', cursor: 'pointer' }}>
//         <canvas ref={canvasRef} style={{ display: 'block' }} />
//       </div>
//     </div>
//   );
// };

// export default React.memo(ProgressScale);